// @file Composable that provides methods for draggable section headers
import device from '@@/bits/device'
import { isSectionGroup, isSingleSelectGroup } from '@@/bits/wish_arrangement'
import { WallWishGroupByValue } from '@@/enums'
import { useSurfacePostGroupByStore } from '@@/pinia/surface_post_group_by_store'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { OnDropPayload } from '@@/pragmatic-drag-and-drop/helpers'
import type { ComponentProps } from '@@/pragmatic-drag-and-drop/renderers'
import { useDraggableElement } from '@@/pragmatic-drag-and-drop/useDraggableElement'
import { useDropTargetElement } from '@@/pragmatic-drag-and-drop/useDropTargetElement'
import type { WallGroupPost } from '@@/types'
import { useSectionSorterHelper } from '@@/vuecomposables/section_sorter_helper'
import { storeToRefs } from 'pinia'
import { computed, nextTick, ref, type Component, type Ref } from 'vue'

export const SURFACE_SECTION_MANUAL_DRAG_PREVIEW_ID = 'surface-section-manual-drag-preview'

export const useSurfaceSectionHeaderDraggableElement = <TDragPreviewComponentProps extends ComponentProps>(params: {
  sectionTitleRef: Ref<HTMLElement | undefined>
  group: WallGroupPost
  type: string
  dragPreview: Component | undefined
  dragPreviewComponentProps: Ref<TDragPreviewComponentProps>
  axis: 'horizontal' | 'vertical'
  isDraggable: Ref<boolean>
  canDrop: Ref<boolean>
  onDragStart?: () => void
  onDragEnd?: () => void
}): {
  groupIdBeingDragged: typeof groupIdBeingDragged
  closestEdgeToPointerForSectionHeader: typeof closestEdgeToPointerForSectionHeader
} => {
  const {
    sectionTitleRef,
    group,
    type,
    dragPreview,
    dragPreviewComponentProps,
    axis,
    isDraggable,
    canDrop,
    onDragStart,
    onDragEnd,
  } = params
  const { isAGroupDragging } = storeToRefs(useSurfacePostGroupByStore())
  const { isGroupBySection, isGroupBySingleSelect, customPropIdFromWishGroupBy } = storeToRefs(
    useSurfaceWishArrangementStore(),
  )
  const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
  const groupIdBeingDragged = ref<string | number | undefined>(undefined)
  const dragPreviewEl = ref<HTMLElement | undefined>(undefined)

  // Make the section title draggable
  useDraggableElement({
    elementRef: sectionTitleRef,
    type,
    data: computed(() => {
      return { section: group }
    }),
    // For iOS, we need to manually create a drag preview element since there is a bug with the native drag preview.
    dragPreview: device.ios ? undefined : dragPreview,
    dragPreviewComponentProps,
    shouldDisableNativeDragPreview: device.ios,
    onDragStart: (e) => {
      isAGroupDragging.value = true
      groupIdBeingDragged.value = group.id
      onDragStart?.()

      if (!device.ios) return
      // For iOS, we need to manually create a drag preview element since there is a bug with the native drag preview.
      // We need to use nextTick to ensure that `sectionTitleRef` is in the DOM before cloning.
      void nextTick(() => {
        if (sectionTitleRef.value == null) return
        const initialX = e.location.initial.input.pageX
        const initialY = e.location.initial.input.pageY
        dragPreviewEl.value = sectionTitleRef.value.cloneNode(true) as HTMLElement
        dragPreviewEl.value.setAttribute('id', SURFACE_SECTION_MANUAL_DRAG_PREVIEW_ID)
        dragPreviewEl.value.style.position = 'absolute'
        dragPreviewEl.value.style.top = `${initialY}px`
        dragPreviewEl.value.style.left = `${initialX}px`
        dragPreviewEl.value.style.width = window.getComputedStyle(sectionTitleRef.value).width
        dragPreviewEl.value.style.opacity = '1'
        document.body.appendChild(dragPreviewEl.value)
      })
    },
    onDrag: (e) => {
      if (!device.ios) return
      // For iOS, we need to manually update the drag preview element to follow the pointer.
      if (dragPreviewEl.value) {
        dragPreviewEl.value.style.top = `${e.location.current.input.pageY}px`
        dragPreviewEl.value.style.left = `${e.location.current.input.pageX}px`
      }
    },
    onDrop: () => {
      isAGroupDragging.value = false
      groupIdBeingDragged.value = undefined
      onDragEnd?.()

      if (!device.ios) return
      // For iOS, we need to manually remove the drag preview element.
      dragPreviewEl.value = undefined
      document.getElementById(SURFACE_SECTION_MANUAL_DRAG_PREVIEW_ID)?.remove()
    },
    isDraggable: computed(() => {
      if (!isDraggable.value) return false
      // Prevent dragging the 'No value' group
      return group.id !== WallWishGroupByValue.NoValue
    }),
  })
  const { closestEdgeToPointer: closestEdgeToPointerForSectionHeader, addDraggableSource } = useDropTargetElement({
    elementRef: sectionTitleRef,
    type,
    data: computed(() => {
      return { section: group }
    }),
  })

  // Make section header droppable
  addDraggableSource({
    type,
    axis,
    onDrop: (e: OnDropPayload<{ section: WallGroupPost }, { section: WallGroupPost }>) => {
      const sourceSection = e.sourceItem.data.section
      const targetSection = e.targetItem.data.section

      isAGroupDragging.value = false
      groupIdBeingDragged.value = undefined

      if (isGroupBySection.value && isSectionGroup(sourceSection) && isSectionGroup(targetSection)) {
        const { sortIndex, gapSize } = useSectionSorterHelper().getSortIndexAndGapSize(
          targetSection.id,
          e.relativePositionToTarget,
        )
        if (sortIndex === undefined || gapSize === undefined) return
        useSurfaceSectionsStore().sortSection({
          sectionId: sourceSection.id,
          sortIndex,
          gapSize,
        })
      } else if (
        isGroupBySingleSelect.value &&
        isSingleSelectGroup(sourceSection) &&
        isSingleSelectGroup(targetSection)
      ) {
        const propertyId = customPropIdFromWishGroupBy.value
        if (propertyId != null) {
          surfacePostPropertiesStore.moveSingleSelectPropertyOptionRelativeTo({
            propertyId,
            sourceOptionId: sourceSection.id,
            targetOptionId: targetSection.id,
            relativePosition: e.relativePositionToTarget,
          })
        }
      }

      if (!device.ios) return
      // For iOS, we need to manually remove the drag preview element.
      dragPreviewEl.value = undefined
      document.getElementById(SURFACE_SECTION_MANUAL_DRAG_PREVIEW_ID)?.remove()
    },
    canDrop: (e) => {
      if (!canDrop.value) return false
      // Prevent dropping to the 'No value' group because the no value group should be the last group
      return e.targetItem.data.section.id !== WallWishGroupByValue.NoValue
    },
  })

  return {
    groupIdBeingDragged,
    closestEdgeToPointerForSectionHeader,
  }
}
